import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import {
  analyticsPath,
  canTrackInteraction,
  trackVisitorEvent,
  type VisitorAnalyticsEvent,
} from '@/lib/visitor-analytics';

const recentPageViews = new Map<string, number>();

function shouldTrackPageView(path: string): boolean {
  const now = Date.now();
  const lastSeen = recentPageViews.get(path) ?? 0;
  recentPageViews.set(path, now);
  return now - lastSeen > 1_000;
}

function interactionEvent(control: Element): VisitorAnalyticsEvent {
  if (
    control.hasAttribute('data-analytics-cta') ||
    control.classList.contains('button--primary') ||
    control.closest('.hero-actions, .final-cta')
  ) {
    return 'cta_click';
  }
  return 'interaction';
}

/**
 * A deliberately lightweight first-party tracker. It records anonymous
 * browser sessions, route views, generic control interactions, and only the
 * time while a page is visible. It never sends form values, IPs, URLs with
 * queries, or referrer paths.
 */
export function AnalyticsTracker() {
  const location = useLocation();
  const pagePath = analyticsPath(location.pathname);

  useEffect(() => {
    if (shouldTrackPageView(pagePath)) {
      trackVisitorEvent('page_view', pagePath);
      if (pagePath === '/register') trackVisitorEvent('signup_start', pagePath);
    }

    let visibleStartedAt = document.visibilityState === 'visible' ? performance.now() : null;

    const flushVisibleTime = () => {
      if (visibleStartedAt === null) return;
      const durationMs = Math.round(performance.now() - visibleStartedAt);
      visibleStartedAt = null;
      if (durationMs >= 1_000) trackVisitorEvent('page_leave', pagePath, durationMs);
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        flushVisibleTime();
      } else if (visibleStartedAt === null) {
        visibleStartedAt = performance.now();
      }
    };

    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('pagehide', flushVisibleTime);
    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('pagehide', flushVisibleTime);
      flushVisibleTime();
    };
  }, [pagePath]);

  useEffect(() => {
    const onClick = (event: MouseEvent) => {
      if (!(event.target instanceof Element)) return;
      const control = event.target.closest('a, button, [role="button"]');
      if (!control || control.closest('[data-analytics-ignore]') || !canTrackInteraction()) return;
      trackVisitorEvent(interactionEvent(control), pagePath);
    };

    document.addEventListener('click', onClick, true);
    return () => document.removeEventListener('click', onClick, true);
  }, [pagePath]);

  return null;
}
